// Incident Management Constants

import { getParticipantLabel } from "Constants/constant";

export const INCIDENT_STATUS = {
  DRAFT: "draft",
  SUBMITTED: "submitted",
  UNDER_INVESTIGATION: "under_investigation",
  CLOSED: "closed",
  REPORTED: "reported",
};

export const INCIDENT_STATUS_LABELS = {
  draft: "Draft",
  submitted: "Submitted",
  under_investigation: "Under Investigation",
  closed: "Closed",
  reported: "Reported",
};

export const INCIDENT_STATUS_OPTIONS = [
  { value: "draft", label: "Draft" },
  { value: "submitted", label: "Submitted" },
  { value: "under_investigation", label: "Under Investigation" },
  { value: "closed", label: "Closed" },
  { value: "reported", label: "Reported" },
];

// Status Badges for UI display
export const STATUS_BADGES = {
  draft: {
    label: "Draft",
    bg: "bg-gray-100",
    text: "text-gray-700",
  },
  submitted: {
    label: "Pending Investigation",
    bg: "bg-yellow-100",
    text: "text-yellow-700",
  },
  under_investigation: {
    label: "Under Investigation",
    bg: "bg-blue-100",
    text: "text-blue-700",
  },
  closed: {
    label: "Closed",
    bg: "bg-green-100",
    text: "text-green-700",
  },
  reported: {
    label: "Reported to NDIS",
    bg: "bg-purple-100",
    text: "text-purple-700",
  },
};

export const OUTCOME_TYPES = {
  HAZARD: "hazard",
  NEAR_MISS: "near_miss",
  INCIDENT: "incident",
  MEDICATION: "medication",
};

export const OUTCOME_OPTIONS = [
  {
    value: "hazard",
    label: "Hazard",
    description: "A condition that could cause harm",
  },
  {
    value: "near_miss",
    label: "Near Miss",
    description: "An incident that could have caused harm but didn't",
  },
  {
    value: "incident",
    label: "Incident",
    description: "An event that caused or could have caused harm",
  },
  {
    value: "medication",
    label: "Medication ",
    description: "Medication-related incident",
  },
];

// === MEDICATION FORM SPECIFICATIONS ===
export const MEDICATION_INCIDENT_CATEGORIES = [
  {
    value: "medication_overdose",
    label: "Medication overdoses due to incorrect administration",
  },
  { value: "split_dropped", label: "Split or dropped medicine" },
  { value: "client_refused", label: "Client refused medication" },
  { value: "incorrect_medicine", label: "Incorrect Medicine" },
  { value: "out_of_date", label: "Out of date medicine" },
  { value: "incorrect_storage", label: "Incorrect storage of medication" },
  { value: "incorrect_dose", label: "Incorrect dose" },
  { value: "missing_medicine", label: "Missing medicine" },
  {
    value: "incorrect_supply",
    label: "Incorrect supply of medication from the pharmacy",
  },
  { value: "incorrect_time", label: "Incorrect Time" },
  {
    value: "request_not_give",
    label: "Request by a client/carer to not give medication",
  },
  {
    value: "lack_documentation",
    label:
      "Lack of documentation such as assessment, medication order, medication support plan, medication record sheet (if required)",
  },
  { value: "incorrect_route", label: "Incorrect Route" },
  {
    value: "breach_policy",
    label: "Breach of the Organisation policy and guidelines",
  },
  { value: "others", label: "Others" },
];

// Legacy alias for backwards compatibility
export const MEDICATION_ERROR_TYPES = MEDICATION_INCIDENT_CATEGORIES;

// === INCIDENT OVERVIEW SPECIFICATIONS ===
export const getPersonInvolvedOptions = (careSector) => [
  { value: "participant", label: getParticipantLabel(careSector, false) },
  { value: "staff", label: "Staff" },
  { value: "visitor", label: "Visitor" },
  { value: "volunteer", label: "Volunteer" },
  { value: "others", label: "Others" },
];

export const PERSON_INVOLVED_OPTIONS = [
  { value: "participant", label: "Participant" },
  { value: "staff", label: "Staff" },
  { value: "visitor", label: "Visitor" },
  { value: "volunteer", label: "Volunteer" },
  { value: "others", label: "Others" },
];

export const INCIDENT_CATEGORIES = [
  { value: "abuse_pwd", label: "Abuse of a PWD" },
  { value: "unlawful_physical_contact", label: "Unlawful Physical Contact" },
  { value: "theft", label: "Theft" },
  { value: "death_pwd", label: "Death of a PWD" },
  { value: "unlawful_sexual_contact", label: "Unlawful Sexual Contact" },
  { value: "alleged_theft", label: "Alleged Theft" },
  { value: "neglect_pwd", label: "Neglect of a PWD" },
  {
    value: "unauthorised_restrictive_practice",
    label: "Unauthorised Restrictive Practice",
  },
  { value: "behaviour", label: "Behaviour" },
  { value: "serious_injury_pwd", label: "Serious Injury of a PWD" },
  { value: "sexual_misconduct", label: "Sexual Misconduct" },
  { value: "non_serious_injury_pwd", label: "Non-serious injury of a PWD" },
  { value: "travel_company_car", label: "Travel (Company Car)" },
  { value: "travel_worker_car", label: "Travel (Worker Car)" },
  { value: "travel_public_transport", label: "Travel (Public Transport)" },
  { value: "others", label: "Others" },
];

/**
 * Analyser incident_draft.incident_category → form checkbox values.
 *
 * Keep in step with ALLOWED_CATEGORIES (incident_draft.py) and CATEGORY_MAP
 * (IncidentDraftFormMapper.php). A category missing here prefills nothing.
 */
export const ANALYSER_CATEGORY_TO_FORM = {
  abuse: "abuse_pwd",
  neglect: "neglect_pwd",
  behaviour: "behaviour",
  injury: "non_serious_injury_pwd",
  serious_injury: "serious_injury_pwd",
  death: "death_pwd",
  theft: "theft",
  alleged_theft: "alleged_theft",
  unlawful_physical_contact: "unlawful_physical_contact",
  unlawful_sexual_contact: "unlawful_sexual_contact",
  sexual_misconduct: "sexual_misconduct",
  restrictive_practice: "unauthorised_restrictive_practice",
  absconding: "others",
  property_damage: "others",
  other: "others",
};

/** Analyser medication_incident_categories → form checkbox values. */
export const ANALYSER_MEDICATION_TO_FORM = {
  wrong_medication: "incorrect_medicine",
  incorrect_medicine: "incorrect_medicine",
  wrong_dose: "incorrect_dose",
  incorrect_dose: "incorrect_dose",
  wrong_time: "incorrect_time",
  incorrect_time: "incorrect_time",
  missed_dose: "missing_medicine",
  missing_medicine: "missing_medicine",
  wrong_participant: "others",
  incorrect_route: "incorrect_route",
  refused: "client_refused",
  client_refused: "client_refused",
  split_dropped: "split_dropped",
  out_of_date: "out_of_date",
  incorrect_storage: "incorrect_storage",
  incorrect_supply: "incorrect_supply",
  request_not_give: "request_not_give",
  lack_documentation: "lack_documentation",
  breach_policy: "breach_policy",
  medication_overdose: "medication_overdose",
  other: "others",
  others: "others",
};

export const INCIDENT_CATEGORY_LABELS = Object.fromEntries(
  INCIDENT_CATEGORIES.map((c) => [c.value, c.label])
);

export const OUTCOME_TYPE_OPTIONS = OUTCOME_OPTIONS;
export const REPORTING_STATUS_OPTIONS = INCIDENT_STATUS_OPTIONS.filter((s) =>
  ["submitted", "under_investigation", "closed"].includes(s.value)
);
export const MAX_VISIBLE_CATEGORIES = 2;

// === INJURY FORM SPECIFICATIONS ===
export const INJURY_CAUSES = [
  { value: "slipping_wet_floor", label: "Slipping on a wet floor" },
  { value: "falling_wheelchair", label: "Falling from a wheelchair" },
  { value: "choking_food", label: "Choking on food" },
  { value: "tripping_clutter", label: "Tripping over clutter" },
  { value: "self_harm", label: "Self-harm injury" },
  { value: "burn_injury", label: "Burn injury" },
  { value: "cuts_sharp_objects", label: "Cuts from sharp objects" },
  { value: "bruising", label: "Bruising" },
  { value: "bed_sores", label: "Bed sores" },
  { value: "other", label: "Other" },
];

export const BODY_PARTS = [
  { value: "head", label: "Head" },
  { value: "neck", label: "Neck" },
  { value: "shoulder_left", label: "Left Shoulder" },
  { value: "shoulder_right", label: "Right Shoulder" },
  { value: "arm_left", label: "Left Arm" },
  { value: "arm_right", label: "Right Arm" },
  { value: "hand_left", label: "Left Hand" },
  { value: "hand_right", label: "Right Hand" },
  { value: "chest", label: "Chest" },
  { value: "back_upper", label: "Upper Back" },
  { value: "back_lower", label: "Lower Back" },
  { value: "abdomen", label: "Abdomen" },
  { value: "hip_left", label: "Left Hip" },
  { value: "hip_right", label: "Right Hip" },
  { value: "leg_left", label: "Left Leg" },
  { value: "leg_right", label: "Right Leg" },
  { value: "knee_left", label: "Left Knee" },
  { value: "knee_right", label: "Right Knee" },
  { value: "foot_left", label: "Left Foot" },
  { value: "foot_right", label: "Right Foot" },
];

// === INVESTIGATION FORM SPECIFICATIONS ===
// Keys must match backend investigation_data JSON field names
export const INVESTIGATION_CHECKS = [
  {
    key: "site_visit_conducted",
    label: "Was a site visit conducted for the incident?",
  },
  {
    key: "routine_activities",
    label: "Did the incident occur during routine activities?",
  },
  {
    key: "equipment_contributed",
    label: "Did the equipment contribute to the incident?",
  },
  {
    key: "equipment_designed_for_task",
    label: "Was the equipment specifically designed for this task?",
  },
  {
    key: "equipment_maintained",
    label: "Was the equipment properly maintained?",
  },
  { key: "equipment_failed", label: "Did the equipment fail?" },
  { key: "risk_assessment_done", label: "Was a risk assessment done?" },
  {
    key: "safety_instructions_provided",
    label: "Were safety instructions provided for the activity?",
  },
  {
    key: "swp_documented",
    label: "Are there documented safe work procedures (SWP) for the activity?",
  },
  { key: "swp_followed", label: "Were the SWP followed?" },
  {
    key: "ppe_used",
    label: "Was appropriate Personal Protective Equipment (PPE) used?",
  },
  {
    key: "person_trained",
    label: "Was the person involved trained in this activity?",
  },
  {
    key: "behavior_issue_contributed",
    label: "Did a known behavior issue contribute?",
  },
  {
    key: "behavior_plan_exists",
    label: "Was there a known behaviour management plan?",
  },
  {
    key: "behavior_plan_followed",
    label: "Was the behaviour management plan followed?",
  },
  {
    key: "housekeeping_contributed",
    label: "Did poor housekeeping contribute?",
  },
  {
    key: "work_environment_contributed",
    label: "Did the work environment contribute?",
  },
];

export const REMEDIAL_ACTIONS = [
  { value: "conduct_task_analysis", label: "Conduct task analysis" },
  { value: "reinstruct_persons", label: "Reinstruct person(s) involved" },
  {
    value: "improve_design",
    label: "Improve design / construction / guarding",
  },
  { value: "conduct_hazard_audit", label: "Conduct hazard systems audit" },
  { value: "improve_skill_mix", label: "Improve Skill Mix" },
  {
    value: "develop_review_procedures",
    label: "Develop / review task procedures",
  },
  {
    value: "provide_debriefing",
    label: "Provide debriefing and/or counselling",
  },
  {
    value: "improve_communication",
    label: "Improve communication / reporting procedures",
  },
  {
    value: "improve_personal_protection",
    label: "Improve personal protection",
  },
  { value: "request_maintenance", label: "Request maintenance" },
  { value: "improve_security", label: "Improve security" },
  { value: "review_whs_policies", label: "Review WHS policies / programs" },
  { value: "housekeeping_review", label: "Housekeeping review" },
  {
    value: "temporarily_relocate",
    label: "Temporarily relocate person involved",
  },
  {
    value: "provide_replace_equipment",
    label: "Provide or replace equipment / tools",
  },
  {
    value: "provide_review_behavior_plan",
    label: "Provide, review or replace Behaviour Support Plan",
  },
  {
    value: "develop_provide_training",
    label: "Develop and/or provide training",
  },
  { value: "improve_work_organisation", label: "Improve work organisation" },
  {
    value: "investigate_safer_alternatives",
    label: "Investigate safer alternatives",
  },
  { value: "request_safety_data_sheet", label: "Request Safety Data Sheet" },
  { value: "eap", label: "EAP" },
  { value: "others", label: "Others" },
];

export const REPORTABLE_INCIDENTS = [
  { value: "death", label: "Death of a person with disability" },
  {
    value: "serious_injury",
    label: "Serious injury of a person with disability",
  },
  {
    value: "abuse_neglect",
    label: "Abuse or neglect of a person with disability",
  },
  { value: "unlawful_conduct", label: "Unlawful sexual or physical conduct" },
  { value: "restrictive_practice", label: "Use of restrictive practice" },
];

export const STEPS = {
  INSTRUCTIONS: 0,
  BASIC_DETAILS: 1,
  OUTCOME_SELECTION: 2,
  INCIDENT_DETAILS: 3,
  ATTACHMENTS: 4,
  REVIEW: 5,
};

export const STEP_LABELS = [
  "Instructions",
  "Basic Details",
  "Outcome Type",
  "Details",
  "Attachments & Signature",
  "Review & Submit",
];

export const INCIDENT_LIST_LIMIT = 10;

// Body parts grouped by anatomical region (for View Incident display)
export const BODY_PART_GROUPS = {
  head: {
    label: "Head & Face",
    parts: [
      "crown",
      "forehead",
      "left-cheek",
      "right-cheek",
      "nose",
      "chin",
      "left-eye",
      "right-eye",
      "upper-lip",
      "lower-lip",
      "right-ear",
      "left-ear",
    ],
  },
  neck: {
    label: "Neck",
    parts: ["anterior-neck", "left-lateral-neck", "right-lateral-neck", "nape"],
  },
  torso: {
    label: "Torso",
    parts: [
      "left-pectoral",
      "right-pectoral",
      "sternum",
      "epigastric",
      "left-hypochondriac",
      "right-hypochondriac",
      "umbilical",
      "left-lumbar",
      "right-lumbar",
      "hypogastric",
      "left-iliac",
      "right-iliac",
      "left-groin",
      "right-groin",
    ],
  },
  back: {
    label: "Back",
    parts: [
      "left-scapula",
      "right-scapula",
      "left-infrascapular",
      "right-infrascapular",
      "left-lumbar-region",
      "right-lumbar-region",
      "lumbar-spine",
      "thoracic-spine",
      "external-occipital-protuberance",
    ],
  },
  arms: {
    label: "Arms & Hands",
    parts: [
      "left-clavicle",
      "right-clavicle",
      "left-shoulder",
      "right-shoulder",
      "left-arm",
      "right-arm",
      "left-elbow",
      "right-elbow",
      "left-forearm",
      "right-forearm",
      "left-wrist",
      "right-wrist",
      "left-hand",
      "right-hand",
    ],
  },
  legs: {
    label: "Legs & Feet",
    parts: [
      "left-thigh",
      "right-thigh",
      "left-knee",
      "right-knee",
      "left-shin",
      "right-shin",
      "left-calf",
      "right-calf",
      "left-ankle",
      "right-ankle",
      "left-foot",
      "right-foot",
    ],
  },
};

export const NDIS_INCIDENT_REQUIREMENTS = `
<strong>NDIS Reportable Incidents</strong>
<p>Under the NDIS Quality and Safeguards Commission, providers must report:</p>
<ul>
  <li>Death of a person with disability</li>
  <li>Serious injury of a person with disability</li>
  <li>Abuse or neglect of a person with disability</li>
  <li>Unlawful sexual or physical contact with a person with disability</li>
  <li>Sexual misconduct committed against a person with disability</li>
  <li>Use of a restrictive practice in relation to a person with disability</li>
</ul>
<p>All incidents must be reported within 24 hours of becoming aware.</p>
`;
